import React, { createContext, useContext, useState, useEffect } from 'react';
import { EntrepriseSettings, entrepriseSettingsMock } from '../mocks/entreprise';
import { api } from '../services/api';

interface BrandingContextType {
  branding: EntrepriseSettings;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<EntrepriseSettings>(() => {
    const saved = localStorage.getItem('atelier_bassoum_entreprise');
    return saved ? JSON.parse(saved) : entrepriseSettingsMock;
  });
  const [loading, setLoading] = useState(true);

  const refreshBranding = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) {
        setLoading(false);
        return;
    }
    
    try {
      const data = await api.getSettings(token);
      if (data) {
        const normalized: EntrepriseSettings = {
          nom: data.nom || 'Atelier Bassoum',
          slogan: data.slogan || '',
          adresse: data.adresse || '',
          telephone: data.telephone || '',
          email: data.email || '',
          logoUrl: data.logo_url || data.logoUrl || '',
          headerText: data.header_text || data.headerText || '',
          footerText: data.footer_text || data.footerText || '',
          nif: data.nif || '',
          rccm: data.rccm || '',
          devise: data.devise || 'FCFA',
          couleurPrimaire: data.couleur_primaire || data.couleurPrimaire || '#4a7c59'
        };
        setBranding(normalized);
        localStorage.setItem('atelier_bassoum_entreprise', JSON.stringify(normalized));
      }
    } catch (err) {
      console.error("Failed to fetch branding settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshBranding();
    
    // Listen for storage changes (e.g. from settings page)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'atelier_bassoum_entreprise' && e.newValue) {
        setBranding(JSON.parse(e.newValue));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Update document title and favicon dynamically
  useEffect(() => {
    if (branding.nom) {
      document.title = `${branding.nom} | ERP`;
    }
    
    if (branding.logoUrl) {
      const link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
      if (link) {
        link.href = api.getProxyUrl(branding.logoUrl);
      } else {
        const newLink = document.createElement('link');
        newLink.rel = 'shortcut icon';
        newLink.href = api.getProxyUrl(branding.logoUrl);
        document.getElementsByTagName('head')[0].appendChild(newLink);
      }
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
